import React from "react";
import { useTranslation } from "react-i18next";
import { SystemNotification } from "@/store/notificationStore";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Bell } from "lucide-react";
import { NotificationCard } from "./NotificationCard";

interface NotificationContentProps {
  isLoading: boolean;
  error: string | null;
  notifications: SystemNotification[];
  activeTab: string;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (notification: SystemNotification) => void;
  onRefresh: () => void;
}

export const NotificationContent: React.FC<NotificationContentProps> = ({
  isLoading,
  error,
  notifications,
  activeTab,
  onMarkAsRead,
  onDelete,
  onNavigate,
  onRefresh
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="p-4">
            <div className="flex items-start space-x-3">
              <Skeleton className="h-5 w-5 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <CardContent className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">{t("notifications.error")}</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={onRefresh}>{t("notifications.retry")}</Button>
        </CardContent>
      </Card>
    );
  }

  if (notifications.length === 0) {
    return (
      <Card className="p-6">
        <CardContent className="text-center">
          <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            {activeTab === "unread" 
              ? t("notifications.noUnreadNotifications")
              : t("notifications.noNotifications")
            }
          </h3>
          <p className="text-gray-600">
            {activeTab === "unread" 
              ? t("notifications.noUnreadNotificationsDescription")
              : t("notifications.noNotificationsDescription")
            }
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-300px)]">
      <div className="space-y-4 pr-4">
        {notifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            onMarkAsRead={onMarkAsRead}
            onDelete={onDelete}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </ScrollArea>
  );
}; 